import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { forkJoin, of, Subscription } from 'rxjs'
import { catchError, finalize, map, switchMap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UserAuthenticationResponseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { confirmMatchingFormFields } from 'src/app/shared/validators'
import { minPasswordLength } from 'src/utils/aws-utils'
import { BaseError, Http401Error } from 'src/utils/errors'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { OnboardingService } from 'src/app/account/services/onboarding.service'
import { CreateAccountService } from '../../services/create-account.service'

@Component({
  selector: 'app-create-account-auth',
  templateUrl: './create-account-auth.component.html',
  styleUrls: ['./create-account-auth.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateAccountAuthComponent implements OnInit {
  createNewUser = true
  errorText = ''
  loginSubscription: Subscription = null
  creatingAccountSubscription: Subscription = null

  private createUserForm = this.formBuilder.group(
    {
      email: ['', [Validators.required, Validators.email]],
      displayName: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(minPasswordLength)]],
      confirmPassword: ['', [Validators.required]],
      agreeToTerms: [false, [Validators.requiredTrue]],
    },
    {
      validators: confirmMatchingFormFields('password', 'confirmPassword'),
    }
  )

  private loginUserForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]], // allow users to provide passwords from before the min length was extended to 12.
  })

  constructor(
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    public uiService: UiService,
    private router: Router,
    private route: ActivatedRoute,
    private createAccountService: CreateAccountService,
    private onboardingService: OnboardingService
  ) {}

  ngOnInit() {
    this.createUserForm.patchValue({
      email: this.createAccountService.accountCreator || '',
    })
    this.loginUserForm.patchValue({
      email: this.createAccountService.accountCreator || '',
    })

    this.route.queryParams.subscribe((params) => {})
  }

  toggleView() {
    this.errorText = ''
    this.createNewUser = !this.createNewUser
  }

  createUserButtonClick() {
    this.errorText = ''
    let userEmail = this.createUserForm.value.email as string
    let displayName = this.createUserForm.value.displayName as string
    let password = this.createUserForm.value.password as string
    this.loginSubscription = this.apiService.registerForNewAccount(
      userEmail,
      displayName,
      password,
      (userAuthDto: UserAuthenticationResponseDto) => {
        this.loginSubscription = null
        this.createAccountService.nativeAuthToken = userAuthDto.nativeAuthToken
        this.createAccountService.accountCreator = userEmail
        // Create account and navigate to landing page
        this.createAccountAndNavigate()
      },
      () => {
        this.loginSubscription = null
        this.errorText = `User ${userEmail} already exists`
      },
      (error) => {
        this.loginSubscription = null
        handleArpioError(window.location.href, this.apiService, this.uiService)(error)
      }
    )
  }

  loginButtonClick() {
    let userEmail = this.loginUserForm.value.email as string
    let password = this.loginUserForm.value.password as string

    this.loginSubscription = this.apiService.loginForNewAccount(
      userEmail,
      password,
      (userAuthDto: UserAuthenticationResponseDto) => {
        this.createAccountService.nativeAuthToken = userAuthDto.nativeAuthToken
        this.createAccountService.accountCreator = userEmail
        this.loginSubscription = null
        // Create account and navigate to landing page
        this.createAccountAndNavigate()
      },
      () => {
        this.loginSubscription = null
        this.errorText = 'Incorrect email or password'
      },
      (error) => {
        this.loginSubscription = null
        handleArpioError(window.location.href, this.apiService, this.uiService)(error)
      }
    )
  }

  get passwordError(): string {
    let passwordLength = this.createUserForm.value['password'].length
    if (passwordLength > 2 && passwordLength < minPasswordLength) {
      return 'Too short'
    }
    return null
  }

  get confirmPasswordError(): string {
    let confirmPasswordLength = this.createUserForm.value['confirmPassword'].length
    if (
      confirmPasswordLength > 5 &&
      this.createUserForm.value['password'] !== this.createUserForm.value['confirmPassword']
    ) {
      return "Doesn't match"
    }
    return null
  }

  get submitting(): boolean {
    return subscriptionActive(this.loginSubscription) || subscriptionActive(this.creatingAccountSubscription)
  }

  private createAccountAndNavigate() {
    // Create the account
    const accountName = `Created by ${this.createAccountService.accountCreator} on ${new Date().toLocaleDateString()}`
    let createAccountObservable = this.apiService.createAccount(
      this.createAccountService.nativeAuthToken,
      accountName,
      true
    )

    // if we have an entitlement token, we need to submit that after we create the account
    if (this.createAccountService.entitlementToken) {
      createAccountObservable = createAccountObservable.pipe(
        switchMap((accountDto) => {
          return forkJoin([
            of(accountDto),
            this.apiService.submitEntitlementToken(accountDto.accountId, this.createAccountService.entitlementToken),
          ])
        }),
        switchMap(([accountDto, _null]) => of(accountDto))
      )
    }

    // Seed onboarding state on the new account so AccountRoot recognizes it as
    // brand-new (not legacy/null-uiState) and routes to the welcome page.
    // Tolerate seeding failures — the createdAccountId fallback in AccountRoot
    // covers this race for the immediate post-creation render.
    this.creatingAccountSubscription = createAccountObservable
      .pipe(
        switchMap((accountDto) =>
          this.onboardingService.seedNewAccount(accountDto.accountId).pipe(
            catchError(() => of(null)),
            map(() => accountDto)
          )
        ),
        finalize(() => (this.creatingAccountSubscription = null))
      )
      .subscribe(
        (accountDto) => {
          this.createAccountService.createdAccountId = accountDto.accountId
          // Navigate to the account root, which will show the landing page
          this.router.navigateByUrl(this.uiService.buildAccountRootUrl(accountDto.accountId))
        },
        (error: BaseError) => {
          if (error instanceof Http401Error) {
            // create account token has expired.  Start over the flow...
            this.router.navigateByUrl(this.uiService.buildCreateAccountUrl())
          } else {
            handleArpioError(window.location.href, this.apiService, this.uiService)(error)
          }
        }
      )
  }
}
